import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Globe, Rows3, Rows2 } from "lucide-react";
import { useLang } from "../../context/LanguageContext";
import { useAuth } from "../../context/AuthContext";
import { useDensity } from "../../context/DensityContext";

export default function Navbar() {
  const { lang, toggleLang, t } = useLang();
  const { user, logout } = useAuth();
  const { density, toggleDensity } = useDensity();
  const [open, setOpen] = useState(false);
  const [acctOpen, setAcctOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const acctRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); setAcctOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!acctOpen) return;
    const onDown = (e) => { if (acctRef.current && !acctRef.current.contains(e.target)) setAcctOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setAcctOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [acctOpen]);

  const initials = (user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const links = [
    { to: "/about", label: t("About", "À propos") },
    { to: "/events", label: t("Events", "Événements") },
    { to: "/community", label: t("Community", "Communauté") },
    { to: "/membership", label: t("Membership", "Adhésion") },
    { to: "/media", label: t("Media", "Médias") },
    { to: "/contact", label: t("Contact", "Contact") },
  ];

  return (
    <div className="fixed top-0 inset-x-0 z-50 px-3 md:px-5 pt-3 md:pt-4">
      <header
        data-testid="site-navbar"
        className={`max-w-7xl mx-auto transition-all duration-300 border ${
          open
            ? "rounded-3xl bg-background border-primary/10 shadow-xl"
            : scrolled
            ? "rounded-full bg-background/85 backdrop-blur-xl border-primary/10 shadow-lg shadow-primary/5"
            : "rounded-full bg-background/60 backdrop-blur-md border-primary/5"
        }`}
      >
        <div className="px-4 md:px-6 py-2.5 flex items-center justify-between gap-4">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 shrink-0">
            <img src="/gybc-logo.png" alt="Got Your Back Club" className="h-14 md:h-[4.25rem] w-auto" />
            <span className="hidden xl:flex items-center pl-3 ml-1 border-l border-primary/15 text-secondary font-semibold tracking-[0.22em] text-sm uppercase whitespace-nowrap">
              Toronto&nbsp;·&nbsp;Canada
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-5 xl:gap-7 rounded-full border border-primary/10 bg-primary/[0.04] px-5 xl:px-7 py-2.5">
            {links.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`nav-${l.to.slice(1)}`}
                className={`link-sweep text-[15px] font-semibold whitespace-nowrap transition-colors ${location.pathname === l.to ? "text-secondary" : "text-primary/85 hover:text-secondary"}`}>
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-2">
            <button data-testid="density-toggle-btn" onClick={toggleDensity} title={t("Content density", "Densité du contenu")}
              className="w-9 h-9 rounded-full flex items-center justify-center text-primary/70 hover:text-secondary hover:bg-primary/5 transition-colors">
              {density === "compact" ? <Rows3 size={17} /> : <Rows2 size={17} />}
            </button>
            <button data-testid="lang-toggle-btn" onClick={toggleLang}
              className="flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-secondary transition-colors px-2">
              <Globe size={15} /> {lang === "en" ? "FR" : "EN"}
            </button>
            {user ? (
              <div className="relative" ref={acctRef}>
                <button data-testid="nav-account-btn" onClick={() => setAcctOpen((v) => !v)}
                  className="w-9 h-9 rounded-full bg-primary text-primary-foreground text-xs font-semibold flex items-center justify-center hover:bg-secondary transition-colors">
                  {initials}
                </button>
                {acctOpen && (
                  <div className="absolute right-0 mt-2 w-56 z-50 rounded-2xl border border-primary/10 bg-background shadow-xl p-2">
                    <div className="px-3 py-2 border-b border-primary/10">
                      <p className="text-sm font-semibold text-primary truncate">{user.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                    </div>
                    <Link to={user.role === "admin" ? "/admin" : "/dashboard"} data-testid="nav-dashboard"
                      className="block px-3 py-2 rounded-xl text-sm font-medium text-primary/90 hover:bg-primary/5">
                      {user.role === "admin" ? t("Admin console", "Console admin") : t("My dashboard", "Mon tableau")}
                    </Link>
                    <Link to={user.role === "admin" ? "/admin/account" : "/account"} data-testid="nav-account-settings"
                      className="block px-3 py-2 rounded-xl text-sm font-medium text-primary/90 hover:bg-primary/5">
                      {t("Account settings", "Paramètres du compte")}
                    </Link>
                    <button data-testid="nav-logout" onClick={() => { logout(); setAcctOpen(false); navigate("/"); }}
                      className="w-full text-left px-3 py-2 rounded-xl text-sm font-medium text-primary/70 hover:bg-primary/5">
                      {t("Sign out", "Déconnexion")}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login" className="text-[15px] font-semibold text-primary/80 hover:text-secondary px-3">{t("Login", "Connexion")}</Link>
                <Link to="/join" data-testid="nav-join-btn" className="group ml-1 rounded-full bg-secondary text-secondary-foreground pl-6 pr-2 py-2 text-[15px] font-semibold hover:bg-primary transition-colors flex items-center gap-2.5 whitespace-nowrap">
                  {t("Join GYBC", "Rejoindre")}
                  <span className="w-8 h-8 rounded-full bg-secondary-foreground/15 flex items-center justify-center group-hover:bg-secondary-foreground/25 transition-colors"><span className="group-hover:translate-x-0.5 transition-transform">↗</span></span>
                </Link>
              </>
            )}
          </div>

          <button data-testid="nav-mobile-toggle" className="lg:hidden text-primary p-1" onClick={() => setOpen(!open)}>
            {open ? <X /> : <Menu />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-primary/10 px-5 py-5 flex flex-col gap-4">
            {links.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`nav-mobile-${l.to.slice(1)}`} className="text-primary font-medium">{l.label}</Link>
            ))}
            <div className="flex items-center gap-4">
              <button onClick={toggleLang} className="flex items-center gap-2 text-primary font-semibold"><Globe size={16} /> {lang === "en" ? "FR" : "EN"}</button>
              <button onClick={toggleDensity} className="flex items-center gap-2 text-primary font-semibold">{density === "compact" ? <Rows3 size={16} /> : <Rows2 size={16} />} {t("Density", "Densité")}</button>
            </div>
            {user ? (
              <>
                <Link to={user.role === "admin" ? "/admin" : "/dashboard"} className="text-primary font-medium">{user.role === "admin" ? t("Admin console", "Console admin") : t("My dashboard", "Mon tableau")}</Link>
                <Link to={user.role === "admin" ? "/admin/account" : "/account"} className="text-primary font-medium">{t("Account settings", "Paramètres du compte")}</Link>
                <button onClick={() => { logout(); navigate("/"); }} className="text-left text-primary/80">{t("Sign out", "Déconnexion")}</button>
              </>
            ) : (
              <>
                <Link to="/login" className="text-primary font-medium">{t("Login", "Connexion")}</Link>
                <Link to="/join" className="rounded-full bg-secondary text-secondary-foreground px-5 py-3 text-center font-semibold">{t("Join GYBC", "Rejoindre")}</Link>
              </>
            )}
          </div>
        )}
      </header>
    </div>
  );
}
